import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Loader2, CheckCircle2, AlertCircle, Clock, Ban } from "lucide-react";
import { maintenanceService } from "../../services/maintenance/planMaestroService";
import toast from "react-hot-toast";

interface CloseOTModalProps {
  open: boolean;
  onClose: () => void;
  ot: any;
  onSuccess?: () => void;
}

export function CloseOTModal({
  open,
  onClose,
  ot,
  onSuccess,
}: CloseOTModalProps) {
  const [loading, setLoading] = useState(false);
  const [paso, setPaso] = useState<1 | 2>(1);

  // Estados de actividades pendientes
  const [pendientes, setPendientes] = useState<any[]>([]);
  const [decisiones, setDecisiones] = useState<Record<number, "postergar" | "cancelar">>({});
  const [justificaciones, setJustificaciones] = useState<Record<number, string>>({});

  // Estados de cierre y variables
  const [kmCierre, setKmCierre] = useState(0);
  const [fechaCierre, setFechaCierre] = useState(new Date().toISOString().split("T")[0]);
  const [observaciones, setObservaciones] = useState("");

  // Estados para Taller, Mecanicos, Costo
  const [talleresList, setTalleresList] = useState<any[]>([]);
  const [selectedTallerId, setSelectedTallerId] = useState<string>("");
  const [mecanicosList, setMecanicosList] = useState<any[]>([]);
  const [selectedMecanicos, setSelectedMecanicos] = useState<number[]>([]);
  const [costoManoObra, setCostoManoObra] = useState<number>(0);

  // Inicializar todo al abrir el modal
  useEffect(() => {
    if (open && ot) {
      // Configuraciones básicas
      setKmCierre(ot.kilometraje_inicio || 0);
      setFechaCierre(new Date().toISOString().split("T")[0]);
      setObservaciones("");
      setCostoManoObra(ot.costo_mano_obra || 0);
      setSelectedTallerId(ot.taller_id ? String(ot.taller_id) : "");
      if (ot.mecanicos) {
        setSelectedMecanicos(ot.mecanicos.map((m: any) => m.id));
      } else {
        setSelectedMecanicos([]);
      }

      // Detectar actividades no realizadas
      const noRealizadas = ot.actividades?.filter((a: any) => !a.realizado) || [];
      setPendientes(noRealizadas);

      if (noRealizadas.length > 0) {
        setPaso(1);
        const defaultDecisiones: Record<number, "postergar" | "cancelar"> = {};
        const defaultJustificaciones: Record<number, string> = {};
        noRealizadas.forEach((a: any) => {
          defaultDecisiones[a.id] = "cancelar";
          defaultJustificaciones[a.id] = "No necesario";
        });
        setDecisiones(defaultDecisiones);
        setJustificaciones(defaultJustificaciones);
      } else {
        setPaso(2); // Ir directo al paso de cierre si no hay pendientes
      }

      fetchTalleres();
    }
  }, [open, ot]);

  // Cargar talleres disponibles
  const fetchTalleres = async () => {
    try {
      const response: any = await maintenanceService.getTalleres();
      setTalleresList(response.data || response);
    } catch (error) {
      console.error("Error al cargar talleres:", error);
    }
  };

  // Cargar mecánicos cuando el taller cambia
  useEffect(() => {
    const fetchMecanicos = async () => {
      if (!selectedTallerId) {
        setMecanicosList([]);
        // Solo limpiar si no es la carga inicial o si realmente quitamos el taller
        setSelectedMecanicos([]);
        return;
      }

      try {
        const res: any = await maintenanceService.getMecanicos(Number(selectedTallerId));
        const list = res.data || res;
        setMecanicosList(list);

        // Si el taller seleccionado es el mismo que el de la OT original,
        // intentamos restaurar los mecánicos que ya estaban asignados.
        if (Number(selectedTallerId) === ot.taller_id && ot.mecanicos) {
          const originalIds = ot.mecanicos.map((m: any) => m.id);
          // Solo actualizamos si la lista actual está vacía o si queremos forzar la sincronización
          // En este caso, si el taller coincide, lo más probable es que queramos ver los que ya estaban.
          setSelectedMecanicos(originalIds);
        } else {
          // Si cambió a un taller distinto del original, ahí sí limpiamos la selección
          // para evitar asignar mecánicos de un taller a otro.
          if (Number(selectedTallerId) !== ot.taller_id) {
            setSelectedMecanicos([]);
          }
        }
      } catch (err) {
        console.error("Error al cargar mecánicos:", err);
        setMecanicosList([]);
      }
    };

    if (open && selectedTallerId) {
      fetchMecanicos();
    } else if (open && !selectedTallerId) {
      setMecanicosList([]);
      setSelectedMecanicos([]);
    }
  }, [selectedTallerId, open, ot.taller_id]);

  // Funciones del paso 1
  const handleDecision = (actividadId: number, decision: "postergar" | "cancelar") => {
    setDecisiones({ ...decisiones, [actividadId]: decision });
  };
  const handleJustificacion = (actividadId: number, texto: string) => {
    setJustificaciones({ ...justificaciones, [actividadId]: texto });
  };
  const handleContinuarPasos1 = () => {
    const sinDecision = pendientes.filter((a) => !decisiones[a.id]);
    if (sinDecision.length > 0) {
      toast.error(`Por favor, decide qué hacer con todas las actividades pendientes`);
      return;
    }
    const canceladasSinJust = pendientes.filter(
      (a) => decisiones[a.id] === "cancelar" && (!justificaciones[a.id] || justificaciones[a.id].trim() === "")
    );
    if (canceladasSinJust.length > 0) {
      toast.error("Las actividades canceladas requieren justificación obligatoria");
      return;
    }
    setPaso(2);
  };

  // Funciones del paso 2 (Guardado Final)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (kmCierre < (ot.kilometraje_inicio || 0)) {
      toast.error(`El kilometraje de cierre no puede ser menor al inicial (${ot.kilometraje_inicio} km)`);
      return;
    }

    if (costoManoObra < 0) {
      toast.error("El costo de mano de obra no puede ser menor a cero");
      return;
    }

    setLoading(true);
    try {
      const postergar = pendientes.filter((a) => decisiones[a.id] === "postergar").map((a) => a.id);
      const cancelar = pendientes.filter((a) => decisiones[a.id] === "cancelar").map((a) => a.id);

      await maintenanceService.closeOT(ot.id, {
        kilometraje_cierre: kmCierre,
        fecha_cierre: fechaCierre,
        observaciones_cierre: observaciones,
        costo_mano_obra: Number(costoManoObra),
        taller_id: selectedTallerId ? Number(selectedTallerId) : null,
        mecanicos_ids: selectedMecanicos,
        actividades_postergar: postergar.length > 0 ? postergar : undefined,
        actividades_cancelar: cancelar.length > 0 ? cancelar : undefined,
        justificaciones: (postergar.length > 0 || cancelar.length > 0) ? justificaciones : undefined,
      });

      toast.success("Orden de Trabajo cerrada correctamente");
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("Error closing OT:", error);
      toast.error("Error al cerrar la Orden de Trabajo");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className={`bg-white dark:bg-slate-950 rounded-lg shadow-xl w-full flex flex-col border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200 ${paso === 1 ? 'max-w-2xl' : 'max-w-md'}`}>
        {/* ENCABEZADO */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-800">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              {paso === 1 ? (
                <AlertCircle className="h-5 w-5 text-orange-600" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              )}
              {paso === 1 ? "Actividades No Realizadas" : "Cerrar Orden de Trabajo"}
            </h2>
            <p className="text-sm text-muted-foreground">{ot.codigo_ot}</p>
          </div>
        </div>

        {/* PASO 1: GESTIÓN DE PENDIENTES */}
        {paso === 1 && (
          <div className="p-5 overflow-y-auto max-h-[70vh] space-y-4">
            <p className="text-sm text-slate-600 mb-4">
              Decide qué hacer con cada actividad pendiente antes de cerrar la orden de trabajo.
            </p>
            <div className="space-y-3">
              {pendientes.map((actividad) => (
                <div
                  key={actividad.id}
                  className={`border-2 rounded-xl p-4 transition-all ${decisiones[actividad.id] === "postergar" ? "border-blue-300 bg-blue-50" :
                    decisiones[actividad.id] === "cancelar" ? "border-red-300 bg-red-50" :
                      "border-gray-200 bg-white"
                    }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900">{actividad.item_catalogo?.nombre}</h4>
                      <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                        <span>Código: {actividad.item_catalogo?.codigo_interno}</span>
                        <span>•</span>
                        <span>Cant: {actividad.cantidad}</span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="space-y-2">
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        className={`w-full ${decisiones[actividad.id] === "postergar" ? "bg-blue-100 border-blue-300 text-blue-700 font-bold" : ""}`}
                        onClick={() => handleDecision(actividad.id, "postergar")}
                      >
                        <Clock className="h-4 w-4 mr-2" />
                        Postergar
                      </Button>
                      <p className="text-[10px] text-blue-600 leading-tight px-1">
                        <b>¿Qué pasa?</b> Se guardará como "Pendiente" para el próximo mantenimiento del vehículo.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        className={`w-full ${decisiones[actividad.id] === "cancelar" ? "bg-red-100 border-red-300 text-red-700 font-bold" : ""}`}
                        onClick={() => handleDecision(actividad.id, "cancelar")}
                      >
                        <Ban className="h-4 w-4 mr-2" />
                        Cancelar
                      </Button>
                      <p className="text-[10px] text-red-600 leading-tight px-1">
                        <b>¿Qué pasa?</b> Se marcará como no necesaria y no se volverá a pedir automáticamente.
                      </p>
                    </div>
                  </div>
                  {decisiones[actividad.id] && (
                    <div className="mt-3">
                      <label className="text-xs font-medium text-gray-700 flex items-center gap-1 mb-2">
                        Justificación {decisiones[actividad.id] === "cancelar" && <span className="text-red-500">*</span>}
                      </label>
                      <Textarea
                        placeholder={
                          decisiones[actividad.id] === "postergar"
                            ? "Ej: Se programará para el próximo mantenimiento..."
                            : "Ej: Ya no es necesaria..."
                        }
                        className="text-sm bg-white"
                        value={justificaciones[actividad.id] || ""}
                        onChange={(e) => handleJustificacion(actividad.id, e.target.value)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-4 mt-6 border-t border-slate-200">
              <Button variant="ghost" onClick={onClose}>Cancelar Cierre</Button>
              <Button onClick={handleContinuarPasos1} className="bg-indigo-600 hover:bg-indigo-700">Continuar →</Button>
            </div>
          </div>
        )}

        {/* PASO 2: DATOS DE CIERRE FINALES */}
        {paso === 2 && (
          <form onSubmit={handleSubmit} className="p-5 flex flex-col max-h-[85vh]">
            <div className="overflow-y-auto pr-2 space-y-4 pb-4">
              {/* Resumen de pendientes en paso 2 */}
              {pendientes.length > 0 && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                  <div className="text-sm text-blue-900 font-medium">
                    {pendientes.length} actividades no realizadas gestionadas.
                  </div>
                  <Button variant="link" size="sm" type="button" onClick={() => setPaso(1)} className="text-blue-700 h-auto p-0">Editar</Button>
                </div>
              )}

              {/* Asignación y Mano de Obra */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-4">
                <h3 className="font-semibold text-sm text-slate-800 border-b border-slate-200 pb-2">Asignación y Costo de Mano de Obra</h3>

                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-600">Taller (Opcional)</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
                    value={selectedTallerId}
                    onChange={(e) => setSelectedTallerId(e.target.value)}
                  >
                    <option value="">-- Sin Taller Asignado --</option>
                    {talleresList.map((t) => (
                      <option key={t.id} value={t.id}>{t.nombre}</option>
                    ))}
                  </select>
                </div>

                {selectedTallerId && (
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-600">Mecánicos (Opcional)</label>
                    {mecanicosList.length === 0 ? (
                      <p className="text-xs text-orange-600 p-2 bg-orange-50 rounded">No hay mecánicos en este taller.</p>
                    ) : (
                      <div className="max-h-32 overflow-y-auto w-full border border-gray-200 rounded-md bg-white p-2 space-y-1">
                        {mecanicosList.map(m => (
                          <label key={m.id} className="flex items-center gap-2 p-1.5 rounded cursor-pointer hover:bg-gray-100">
                            <input
                              type="checkbox"
                              checked={selectedMecanicos.includes(m.id)}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedMecanicos(p => [...p, m.id]);
                                else setSelectedMecanicos(p => p.filter(id => id !== m.id));
                              }}
                              className="rounded text-indigo-600 cursor-pointer"
                            />
                            <span className="text-xs text-gray-700">{m.nombre}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-900">Costo Mano de Obra (S/)</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={costoManoObra}
                    onChange={(e) => setCostoManoObra(Number(e.target.value))}
                    className="border-indigo-300 focus-visible:ring-indigo-500 font-bold"
                  />
                  <p className="text-[10px] text-slate-500">Monto total a cobrar por el servicio de mantenimiento.</p>
                </div>
              </div>

              {/* Datos Generales de Cierre */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-600">Fecha Cierre <span className="text-red-500">*</span></label>
                    <Input type="date" required value={fechaCierre} onChange={(e) => setFechaCierre(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-600">Km Final <span className="text-red-500">*</span></label>
                    <Input type="number" required min={ot.kilometraje_inicio} value={kmCierre} onChange={(e) => setKmCierre(Number(e.target.value))} />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-600">Observaciones Finales</label>
                  <Textarea
                    placeholder="Comentarios sobre el trabajo realizado..."
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Acciones */}
            <div className="flex justify-between gap-3 pt-4 border-t border-slate-200 mt-2">
              <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
                Cancelar
              </Button>
              <div className="flex gap-2">
                {pendientes.length > 0 && (
                  <Button type="button" variant="outline" onClick={() => setPaso(1)} disabled={loading}>
                    ← Atrás
                  </Button>
                )}
                <Button type="submit" className="bg-green-600 hover:bg-green-700" disabled={loading}>
                  {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Confirmar Cierre
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
